import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Save, Trash2, X, Check } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/formatDate";

const FIELDS = [
    { key: "new_target_brokers", label: "New Targets" },
    { key: "outreach", label: "Outreach" },
    { key: "follow_ups", label: "Follow-Ups" },
    { key: "conversations", label: "Conversations" },
    { key: "new_active", label: "New Active" },
    { key: "submissions", label: "Submissions ($25k+)" },
];

export default function WeeklyKPIs() {
    const [rows, setRows] = useState([]);
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState({ week_of: "", new_target_brokers: 0, outreach: 0, follow_ups: 0, conversations: 0, new_active: 0, submissions: 0 });
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState({});

    const load = () => api.get("/weekly-kpis").then((r) => setRows(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const totals = rows.reduce((acc, r) => {
        FIELDS.forEach(f => acc[f.key] = (acc[f.key] || 0) + (r[f.key] || 0));
        return acc;
    }, {});

    const submitNew = async () => {
        if (!draft.week_of) { toast.error("Pick a week start date"); return; }
        try {
            await api.post("/weekly-kpis", { ...draft });
            toast.success("Week added");
            setAdding(false);
            setDraft({ week_of: "", new_target_brokers: 0, outreach: 0, follow_ups: 0, conversations: 0, new_active: 0, submissions: 0 });
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
        }
    };

    const saveEdit = async (id) => {
        try {
            await api.patch(`/weekly-kpis/${id}`, editDraft);
            toast.success("Updated");
            setEditingId(null);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this week's KPI row?")) return;
        try {
            await api.delete(`/weekly-kpis/${id}`);
            toast.success("Deleted");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const fmt = (d) => fmtDate(d);

    return (
        <div className="p-6 lg:p-8 space-y-5 animate-fade-in" data-testid="weekly-kpis-page">
            <div className="flex justify-between items-end gap-3 flex-wrap">
                <div>
                    <h1 className="ph-h1">Weekly KPIs</h1>
                    <p className="text-sm text-ph-muted mt-1">A simple weekly performance log used in the Friday review.</p>
                </div>
                <Button onClick={() => setAdding(true)} className="bg-ph-primary text-white font-medium h-9 px-3 hover:bg-ph-primaryHover" data-testid="add-week-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Add week
                </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
                <div className="lg:col-span-3 ph-card overflow-hidden">
                    <div className="overflow-auto scrollbar-thin">
                        <table className="w-full text-sm" data-testid="weekly-kpis-table">
                            <thead className="bg-ph-canvas text-[11px] font-medium text-ph-muted border-b border-ph-border">
                                <tr>
                                    <th className="text-left px-4 py-2.5">Week of</th>
                                    {FIELDS.map(f => <th key={f.key} className="text-right px-4 py-2.5">{f.label}</th>)}
                                    <th className="px-4 py-2.5"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {adding && (
                                    <tr className="border-t border-ph-borderSoft bg-ph-primarySoft/40">
                                        <td className="px-4 py-2"><Input type="date" value={draft.week_of} onChange={(e) => setDraft({ ...draft, week_of: e.target.value })} className="h-9" data-testid="week-of-input" /></td>
                                        {FIELDS.map(f => (
                                            <td key={f.key} className="px-4 py-2 text-right">
                                                <Input type="number" value={draft[f.key]} onChange={(e) => setDraft({ ...draft, [f.key]: parseInt(e.target.value) || 0 })} className="h-9 text-right" data-testid={`new-${f.key}-input`} />
                                            </td>
                                        ))}
                                        <td className="px-4 py-2 flex gap-1">
                                            <button onClick={submitNew} className="text-emerald-600 hover:text-emerald-700" data-testid="confirm-add-week"><Check className="w-4 h-4" /></button>
                                            <button onClick={() => setAdding(false)} className="text-ph-mutedSoft hover:text-ph-muted"><X className="w-4 h-4" /></button>
                                        </td>
                                    </tr>
                                )}
                                {rows.length === 0 && !adding && (
                                    <tr><td colSpan={FIELDS.length + 2} className="text-center py-10 text-ph-muted">
                                        No weeks tracked yet. Add your first week to start the cadence.
                                    </td></tr>
                                )}
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-t border-ph-borderSoft hover:bg-ph-canvas" data-testid={`kpi-row-${r.id}`}>
                                        <td className="px-4 py-3 font-medium text-ph-text whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <span>{fmt(r.week_of)}</span>
                                                {r.auto_seeded && (
                                                    <span
                                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-semibold"
                                                        title="Auto-seeded from this week's live activity. Edit to confirm."
                                                        data-testid={`auto-draft-pill-${r.id}`}
                                                    >
                                                        Auto-draft
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        {FIELDS.map(f => (
                                            <td key={f.key} className="px-4 py-3 text-right tabular-nums text-ph-text">
                                                {editingId === r.id ? (
                                                    <Input type="number" value={editDraft[f.key]} onChange={(e) => setEditDraft({ ...editDraft, [f.key]: parseInt(e.target.value) || 0 })} className="h-8 text-right w-20 ml-auto" />
                                                ) : (r[f.key] || 0)}
                                            </td>
                                        ))}
                                        <td className="px-4 py-3 flex gap-2 justify-end items-center">
                                            {editingId === r.id ? (
                                                <>
                                                    <button onClick={() => saveEdit(r.id)} className="text-emerald-600" data-testid={`save-edit-${r.id}`}><Save className="w-4 h-4" /></button>
                                                    <button onClick={() => setEditingId(null)} className="text-ph-mutedSoft"><X className="w-4 h-4" /></button>
                                                </>
                                            ) : (
                                                <>
                                                    <button onClick={() => { setEditingId(r.id); setEditDraft({ ...r }); }} className="text-ph-primary text-xs font-medium hover:text-ph-primaryHover" data-testid={`edit-week-${r.id}`}>Edit</button>
                                                    <button onClick={() => remove(r.id)} className="text-rose-500 hover:text-rose-600" data-testid={`delete-week-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {rows.length > 0 && (
                                    <tr className="border-t border-ph-border bg-ph-canvas font-medium">
                                        <td className="px-4 py-3 text-ph-text">Totals</td>
                                        {FIELDS.map(f => <td key={f.key} className="px-4 py-3 text-right tabular-nums text-ph-text font-semibold">{totals[f.key] || 0}</td>)}
                                        <td></td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <aside className="ph-card p-5 space-y-3 self-start">
                    <h2 className="ph-h2">Cumulative totals</h2>
                    {FIELDS.map(f => (
                        <div key={f.key} className="flex justify-between items-center border-b border-ph-borderSoft pb-2 last:border-0">
                            <span className="text-xs text-ph-muted">{f.label}</span>
                            <span className="text-lg font-semibold tabular-nums text-ph-text">{totals[f.key] || 0}</span>
                        </div>
                    ))}
                </aside>
            </div>
        </div>
    );
}
